import type { ErrorObject, ValidateFunction } from "ajv";
import Ajv from "ajv/dist/2020";
import addFormats from "ajv-formats";
import { get } from "lodash";

import { compileSchema } from "./compile";
import { FieldNode, OneOfNode, Schema, SchemaError } from "./types";

/*
 * Validates a value against a JSON Schema with one compiled validator per schema object,
 * and turns Ajv's errors into `path: message` pairs the form can show next to a field.
 */

const ajv = new Ajv({ allErrors: true });
addFormats(ajv);

const validators = new WeakMap<Schema, ValidateFunction>();

export const getValidator = (schema: Schema): ValidateFunction => {
  const cached = validators.get(schema);
  if (cached) return cached;

  const validator = ajv.compile(schema);
  validators.set(schema, validator);
  return validator;
};

const toPath = (instancePath: string) =>
  instancePath
    .split("/")
    .filter(Boolean)
    .map((part) => part.replace(/~1/g, "/").replace(/~0/g, "~"))
    .join(".");

const join = (path: string, key: string) => (path ? `${path}.${key}` : key);

const errorPath = (error: ErrorObject) => {
  const path = toPath(error.instancePath);

  if (error.keyword === "required") {
    return join(path, String(error.params.missingProperty));
  }

  return path;
};

const collectOneOfNodes = (node: FieldNode, into: OneOfNode[]) => {
  switch (node.kind) {
    case "oneOf":
      into.push(node);
      node.options.forEach((option) => collectOneOfNodes(option.node, into));
      break;
    case "object":
      node.children.forEach((child) => collectOneOfNodes(child, into));
      break;
    case "union":
      node.options.forEach((option) => collectOneOfNodes(option.node, into));
      break;
    case "array":
      collectOneOfNodes(node.item, into);
      break;
    default:
      break;
  }
};

const isUnder = (path: string, parent: string) =>
  parent === "" || path === parent || path.startsWith(`${parent}.`);

/*
 * When no branch of a union matches, Ajv reports the errors of every branch.
 * Only the errors of the branch selected by the discriminator are kept.
 */
const dropOtherBranches = (
  errors: ErrorObject[],
  schema: Schema,
  value: unknown,
): ErrorObject[] => {
  const unions: OneOfNode[] = [];
  collectOneOfNodes(compileSchema(schema), unions);

  if (unions.length === 0) return errors;

  return errors.filter((error) => {
    const path = toPath(error.instancePath);
    const union = unions
      .filter((node) => isUnder(path, node.path))
      .sort((a, b) => b.path.length - a.path.length)[0];

    if (!union) return true;

    const current = union.path ? get(value, union.path) : value;
    const selected = union.options.find(
      (option) => option.value === get(current, union.discriminator),
    );

    if (!selected) return true;

    if (error.keyword === "oneOf" && path === union.path) return false;

    return !union.options.some(
      (option) =>
        option !== selected &&
        option.ref &&
        error.schemaPath.includes(`/${option.ref}/`),
    );
  });
};

// Errors that only repeat others: the `null` branch of an optional value, and the `anyOf` summary
const isRepeat = (error: ErrorObject) =>
  error.keyword === "anyOf" ||
  (error.keyword === "type" && error.params.type === "null");

export const validateAgainstSchema = (
  schema: Schema,
  value: unknown,
): SchemaError[] => {
  const validator = getValidator(schema);

  if (validator(value) || !validator.errors) {
    return [];
  }

  const errors = dropOtherBranches(validator.errors, schema, value);
  const shown = errors.filter((error) => !isRepeat(error));

  return (shown.length > 0 ? shown : errors).map((error) => ({
    path: errorPath(error),
    message: error.message ?? error.keyword,
  }));
};

export const errorsByPath = (errors: SchemaError[]): Record<string, string> =>
  errors.reduce<Record<string, string>>((result, error) => {
    if (!(error.path in result)) {
      result[error.path] = error.message;
    }
    return result;
  }, {});
